import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { ShoppingCart, Search } from '@mui/icons-material';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const RateShopping = () => {
  const { getAuthToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [loadingOrigins, setLoadingOrigins] = useState(false);
  const [rates, setRates] = useState([]);
  const [origins, setOrigins] = useState([]);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    selectedOrigin: '',
    destinationZip: '',
    weight: 1,
    length: 10,
    width: 8,
    height: 6
  });

  // Fetch origins on component mount
  useEffect(() => {
    const fetchOrigins = async () => {
      setLoadingOrigins(true);
      setError(''); // Clear any previous errors
      try {
        const token = getAuthToken();
        
        if (!token) {
          console.error('No auth token - user not authenticated');
          setError('Please log in to load origins');
          setLoadingOrigins(false);
          return;
        }

        console.log('Auth token:', token.substring(0, 30) + '...');
        console.log('Fetching origins from backend...');
        
        const response = await axios.get('/auth/origins', {
          headers: {
            'Authorization': token
          }
        });

        console.log('Origins response:', response.data);

        if (response.data && response.data.results) {
          console.log(`Found ${response.data.results.length} origins`);
          setOrigins(response.data.results);
          // Auto-select first origin if available
          if (response.data.results.length > 0) {
            setFormData(prev => ({
              ...prev,
              selectedOrigin: response.data.results[0].originId
            }));
          }
        } else {
          console.warn('No results in origins response');
          setError('No origins found for your account');
        }
      } catch (error) {
        console.error('Error fetching origins:', error);
        console.error('Error details:', error.response?.data || error.message);
        setError(`Failed to load origins: ${error.response?.data?.message || error.message}`);
      } finally {
        setLoadingOrigins(false);
      }
    };

    fetchOrigins();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const getSelectedOrigin = () => {
    return origins.find(o => o.originId === formData.selectedOrigin);
  };

  const handleGetRates = async () => {
    if (!formData.selectedOrigin || !formData.destinationZip) {
      setError('Please select an origin and enter a destination ZIP code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const token = getAuthToken();
      
      if (!token) {
        setError('Please log in to get rates');
        setLoading(false);
        return;
      }

      // Get the selected origin
      const selectedOrigin = getSelectedOrigin();
      if (!selectedOrigin) {
        setError('Please select a valid origin');
        setLoading(false);
        return;
      }

      // Build a complete Shipium-compatible payload for rate shopping
      const payload = {
        currencyCode: 'USD',
        includeEvaluatedServiceMethodsInResponse: true,
        includeLineItemsInEvaluatedServiceMethods: true,
        shipmentParameters: {
          orderedDateTime: new Date().toISOString(),
          shippedDateTime: new Date().toISOString(),
          orderItemQuantities: [
            {
              productId: 'Product',
              quantity: 1
            }
          ],
          packagingType: {
            packagingMaterial: 'box',
            packagingSizeName: 'custom',
            linearDimensions: {
              linearUnit: 'in',
              length: formData.length,
              width: formData.width,
              height: formData.height
            },
            packagingWeight: {
              weightUnit: 'lb',
              weight: formData.weight
            }
          },
          destinationAddress: {
            address1: '123 Main St',
            city: 'City',
            state: 'CA',
            zip: formData.destinationZip,
            countryCode: 'US'
          },
          shipFromAddress: {
            address1: selectedOrigin.address.street1,
            address2: selectedOrigin.address.street2,
            city: selectedOrigin.address.city,
            state: selectedOrigin.address.state,
            zip: selectedOrigin.address.postalCode,
            countryCode: selectedOrigin.address.countryCode,
            company: selectedOrigin.address.company,
            name: selectedOrigin.address.name
          }
        },
        referenceIdentifier: `RATE-${Date.now()}`
      };

      // Use API token from environment for Shipium API calls
      const apiToken = process.env.REACT_APP_API_TOKEN;
      const apiAuthorization = apiToken 
        ? (apiToken.startsWith('Basic ') ? apiToken : `Basic ${apiToken}`)
        : token;
      
      const response = await axios.post('/api/v1/carrierselection/rateshop', payload, {
        headers: {
          'Authorization': apiAuthorization
        }
      });
      
      // The response structure depends on the Shipium API
      // It might return carrierSelection with service options
      if (response.data && response.data.carrierSelection && response.data.carrierSelection.serviceOptions) {
        setRates(response.data.carrierSelection.serviceOptions);
      } else if (response.data && response.data.rates) {
        setRates(response.data.rates);
      } else {
        setRates([]);
        setError('No rates found for this shipment');
      }
    } catch (error) {
      setError(error.response?.data?.message || error.message || 'Failed to get rates');
      console.error('Error getting rates:', error);
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const selectRate = (rate) => {
    console.log('Rate selected:', rate);
  };

  return (
    <Box sx={{ width: '100%', height: '100%', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <ShoppingCart sx={{ mr: 2, color: 'primary.main' }} />
        <Typography variant="h4">Rate Shopping</Typography>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Shipment Details
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Ship From Origin</InputLabel>
                    <Select
                      value={formData.selectedOrigin}
                      onChange={(e) => handleInputChange('selectedOrigin', e.target.value)}
                      label="Ship From Origin"
                      disabled={loadingOrigins}
                    >
                      {loadingOrigins ? (
                        <MenuItem value="">
                          <CircularProgress size={20} /> Loading origins...
                        </MenuItem>
                      ) : origins.length === 0 ? (
                        <MenuItem value="">No origins available</MenuItem>
                      ) : (
                        origins.map((origin) => (
                          <MenuItem key={origin.originId} value={origin.originId}>
                            {origin.name} - {origin.address.city}, {origin.address.state} {origin.address.postalCode}
                          </MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Destination ZIP Code"
                    value={formData.destinationZip}
                    onChange={(e) => handleInputChange('destinationZip', e.target.value)}
                    placeholder="67890"
                    variant="outlined"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Weight (lbs)"
                    type="number"
                    value={formData.weight}
                    onChange={(e) => handleInputChange('weight', parseFloat(e.target.value))}
                    variant="outlined"
                    inputProps={{ min: 0.1, max: 150, step: 0.1 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Length (in)"
                    type="number"
                    value={formData.length}
                    onChange={(e) => handleInputChange('length', parseInt(e.target.value))}
                    variant="outlined"
                    inputProps={{ min: 1, max: 108 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Width (in)"
                    type="number"
                    value={formData.width}
                    onChange={(e) => handleInputChange('width', parseInt(e.target.value))}
                    variant="outlined"
                    inputProps={{ min: 1, max: 108 }}
                  />
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Height (in)"
                    type="number"
                    value={formData.height}
                    onChange={(e) => handleInputChange('height', parseInt(e.target.value))}
                    variant="outlined"
                    inputProps={{ min: 1, max: 108 }}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    size="large"
                    startIcon={loading ? <CircularProgress size={20} /> : <Search />}
                    onClick={handleGetRates}
                    disabled={loading}
                    sx={{ mt: 2 }}
                  >
                    {loading ? 'Getting Rates...' : 'Get Rates'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Available Rates
              </Typography>
              
              {rates.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Service</TableCell>
                        <TableCell>Cost</TableCell>
                        <TableCell>Delivery Date</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rates.map((rate, index) => (
                        <TableRow key={index} hover>
                          <TableCell>
                            {rate.serviceName || rate.service || rate.serviceLevel || 'Unknown Service'}
                            {rate.carrier && ` (${rate.carrier})`}
                          </TableCell>
                          <TableCell>
                            {formatCurrency(
                              rate.totalCharges || 
                              rate.cost || 
                              rate.amount || 
                              rate.totalAmount || 
                              0
                            )}
                          </TableCell>
                          <TableCell>
                            {rate.deliveryDate 
                              ? formatDate(rate.deliveryDate) 
                              : rate.deliveryDays 
                              ? `${rate.deliveryDays} days` 
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => selectRate(rate)}
                            >
                              Select
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    Enter shipment details and click "Get Rates" to see available options
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RateShopping;



